"use client";

import { useIsWindows } from "@/hooks";
import { EmojiSelect, Select } from "@/components/ui";
import { EmojiIcon } from "@/components/ui/emoji/EmojiIcon";
import { capture } from "@/lib/analytics/posthog";
import {
  getTopSellersRegionIconOptions,
  getTopSellersRegionSelectOptions,
  normalizeTopSellersRegion,
} from "@/features/topsellers/config/regions";

const REGION_SELECT_CLASS =
  "h-8 min-w-[150px] rounded-lg text-[11px] font-medium sm:h-9 sm:min-w-[190px] sm:text-xs md:text-sm";

type RegionSelectProps = {
  value: string;
  onChange: (cc: string) => void;
  /** Hide the "global" option — New & Trending has no global list. */
  excludeGlobal?: boolean;
  className?: string;
  /** Which chart this picker drives — labels the analytics event. */
  chart?: string;
};

/**
 * Country/region picker shared by the rank-history charts (Top Sellers, New &
 * Trending) and the Compare page. On Windows it renders twemoji flags via
 * EmojiSelect (native emoji flags are broken there); everywhere else a native
 * Select with the flag in the label. `onChange` always receives a validated cc
 * (normalised against TOPSELLERS_REGIONS — an unknown value collapses to "global").
 */
export function RegionSelect({
  value,
  onChange,
  excludeGlobal = false,
  className = REGION_SELECT_CLASS,
  chart = "unknown",
}: RegionSelectProps) {
  const isWindows = useIsWindows();
  const handleChange = (cc: string) => {
    const normalised = normalizeTopSellersRegion(cc);
    capture("chart region changed", { chart, cc: normalised });
    onChange(normalised);
  };

  if (isWindows) {
    return (
      <EmojiSelect
        value={value}
        onChange={handleChange}
        ariaLabel="Region"
        className={className}
        options={getTopSellersRegionIconOptions()
          .filter((o) => !excludeGlobal || o.value !== "global")
          .map((o) => ({
            value: o.value,
            label: o.label,
            icon: <EmojiIcon emoji={o.emoji} size={18} title={o.label} />,
          }))}
      />
    );
  }

  return (
    <Select
      value={value}
      onChange={handleChange}
      ariaLabel="Region"
      className={className}
      options={getTopSellersRegionSelectOptions().filter(
        (o) => !excludeGlobal || o.value !== "global",
      )}
    />
  );
}
